import { useState } from "react";
import { AnimatePresence, m } from "framer-motion";

import { useCreateChangeRequestFromScopeViolation } from "@/api/hooks";
import { useToast } from "@/components/ui/toast";
import { useModalA11y } from "@/hooks/useModalA11y";
import { useProjectStore } from "@/stores/project";
import type { ScopeBlockPayload } from "@/stores/scopeBlock";
import { useScopeBlockStore } from "@/stores/scopeBlock";

const FIELD_CLASS =
  "mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100";

function prettyField(field: string): string {
  return field.replace(/_id$/, "").replace(/_/g, " ");
}

/**
 * Inner form — remounted per scope-block (keyed on the store's `seq`) so the
 * editable fields always start fresh from the prefill the backend supplied,
 * even for a second block on the same entity.
 */
function ScopeChangeForm({ block }: { block: ScopeBlockPayload }) {
  const dismiss = useScopeBlockStore((s) => s.dismiss);
  const projectId = useProjectStore((s) => s.selectedProjectId);
  const toast = useToast();
  const createCR = useCreateChangeRequestFromScopeViolation();

  const prefill = block.change_request_prefill;
  const [title, setTitle] = useState(prefill.title);
  const [description, setDescription] = useState(prefill.description);
  const [justification, setJustification] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleCreate() {
    if (!projectId) return;
    setErrorMsg(null);
    const base = description.trim() || prefill.description;
    const changeDescription = justification.trim()
      ? `${base}\n\nJustification: ${justification.trim()}`
      : base;
    try {
      // The from-scope-violation endpoint runs impact analysis and stamps
      // scope_violation_source — a higher-fidelity CR than a generic create.
      await createCR.mutateAsync({
        project_id: projectId,
        baseline_id: prefill.baseline_id,
        target_entity_type: prefill.target_entity_type,
        target_entity_id: prefill.target_entity_id,
        change_description: changeDescription,
        title: title.trim() || prefill.title,
      });
      toast.success(
        "Change request created",
        "Your scope change is now tracked in change control.",
      );
      dismiss();
    } catch {
      setErrorMsg(
        "Could not create the change request. Please try again, or open Change Control.",
      );
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-3 border-b border-slate-200 px-6 py-5 dark:border-slate-700">
        <span
          className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-lg dark:bg-amber-500/20"
          aria-hidden
        >
          🔒
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Change request required
          </h2>
          <p className="mt-0.5 truncate text-sm text-slate-500 dark:text-slate-400">
            Baseline {block.baseline_code} — {block.baseline_name}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700"
        >
          ✕
        </button>
      </div>

      <div className="space-y-4 px-6 py-5">
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          {block.message}
        </p>

        {block.scope_fields.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {block.scope_fields.map((f) => (
              <span
                key={f}
                className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30"
              >
                {prettyField(f)}
              </span>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-500 dark:text-slate-400">
          A baseline protects committed scope. Raise a change request to carry
          this change through review.
        </p>

        <div className="space-y-3 rounded-xl bg-slate-50 p-4 dark:bg-slate-800/60">
          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Change request title
            </span>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={FIELD_CLASS}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Description
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`${FIELD_CLASS} resize-y`}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Justification{" "}
              <span className="font-normal text-slate-400">(optional)</span>
            </span>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={2}
              placeholder="Why is this change needed?"
              className={`${FIELD_CLASS} resize-y placeholder:text-slate-400`}
            />
          </label>
        </div>

        {errorMsg && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {errorMsg}
          </p>
        )}
        {!projectId && (
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
            Open a project to raise a change request.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
        <button
          type="button"
          onClick={dismiss}
          disabled={createCR.isPending}
          className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={createCR.isPending || !title.trim() || !projectId}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {createCR.isPending && (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          )}
          {createCR.isPending ? "Creating…" : "Create change request"}
        </button>
      </div>
    </div>
  );
}

/**
 * App-level modal that renders whenever the scope-aware baseline gate blocks a
 * change. Driven by useScopeBlockStore, which the HTTP client populates on a
 * 423 scope-block response. Mounted once, at the App root.
 */
export function ScopeChangeModal() {
  const block = useScopeBlockStore((s) => s.block);
  const seq = useScopeBlockStore((s) => s.seq);
  const dismiss = useScopeBlockStore((s) => s.dismiss);

  // Escape + focus-trap + focus-restore + scroll-lock on the inner panel.
  const panelRef = useModalA11y<HTMLDivElement>({ open: !!block, onClose: dismiss });

  return (
    <AnimatePresence>
      {block && (
        <m.div
          key="scope-block-backdrop"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={dismiss}
        >
          <m.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Change request required"
            tabIndex={-1}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900"
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ScopeChangeForm key={seq} block={block} />
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
